<template>
  <div class="questionnaire-item" :class="{ 'has-error': hasError }">
    <p class="question-title">
      <span v-if="question.required" class="required-mark">*</span><span class="question-number">{{ questionNumber }}.</span>
      <span class="question-text" v-if="!isTimeInput && !isUnderlineInput">{{ displayTitle }}</span>
      <span class="question-text question-text-with-inputs" v-else-if="isTimeInput">
        <template v-for="(part, index) in timeInputTitleParts" :key="index">
          <span v-if="part.type === 'text'">{{ part.content }}</span>
          <input
            v-else-if="part.type === 'hours'"
            type="number"
            v-model.number="timeHours"
            @input="handleHoursInput"
            @blur="validateHours"
            placeholder=""
            class="time-input-inline"
            inputmode="numeric"
          />
          <input
            v-else-if="part.type === 'minutes'"
            type="number"
            v-model.number="timeMinutes"
            @input="handleMinutesInput"
            @blur="validateMinutes"
            placeholder=""
            class="time-input-inline"
            inputmode="numeric"
          />
        </template>
      </span>
      <span class="question-text question-text-with-inputs" v-else-if="isUnderlineInput">
        <template v-for="(part, index) in underlineInputTitleParts" :key="index">
          <span v-if="part.type === 'text'">{{ part.content }}</span>
          <input
            v-else-if="part.type === 'number'"
            type="number"
            v-model.number="localAnswer"
            :min="question.numberConfig?.min || 0"
            :max="question.numberConfig?.max"
            @input="handleNumberInput"
            @blur="validateNumberInput"
            placeholder=""
            class="underline-input-inline"
            inputmode="numeric"
          />
        </template>
      </span>
      <span v-if="hasError" class="error-hint">（请填写此项）</span>
    </p>
    
    <!-- 单选题 -->
    <div v-if="question.type === 'SINGLE_CHOICE'" class="single-choice-wrapper">
      <div
        v-for="(opt, idx) in question.options"
        :key="idx"
        class="single-choice-option"
        :class="{ 'selected': getSelectedOptionValue() === opt.value, 'has-custom-input': opt.allowCustomInput }"
        @click="selectSingleChoice(opt.value, opt.allowCustomInput)"
      >
        <div class="option-content">
        <span class="option-label">{{ String.fromCharCode(65 + idx) }}. {{ opt.label }}</span>
          <span class="option-radio" :class="{ 'checked': getSelectedOptionValue() === opt.value }"></span>
        </div>
        <!-- 自定义输入框（仅在该选项允许自定义输入且被选中时显示） -->
        <div v-if="opt.allowCustomInput && getSelectedOptionValue() === opt.value" class="custom-input-wrapper" @click.stop>
          <input
            type="text"
            v-model="customInputValue"
            @input="handleCustomInputChange"
            @click.stop
            placeholder="请填写具体内容"
            class="custom-input"
            ref="customInput"
          />
        </div>
      </div>
    </div>
    
    <!-- 多选题 -->
    <el-checkbox-group
      v-if="question.type === 'MULTIPLE_CHOICE'"
      v-model="localAnswer"
      @change="handleAnswerChange"
    >
      <el-checkbox
        v-for="(opt, idx) in question.options"
        :key="idx"
        :label="opt.value"
      >
        {{ String.fromCharCode(65 + idx) }}. {{ opt.label }}
      </el-checkbox>
    </el-checkbox-group>
    
    <!-- 评分题（0-10分横向滑块） -->
    <div v-if="question.type === 'RATING'" class="rating-question" :class="{ 'pain-rating-disabled': isPainRatingDisabled }">
      <!-- 疼痛评分题提示：如果关联的是否题选"否"，显示提示 -->
      <div v-if="question.painRatingRelated?.isRatingQuestion && isPainRatingDisabled" class="pain-rating-hint">
        当上一题选择"否"，本题无需进行填写
      </div>
      <div v-if="question.ratingConfig?.hint" class="rating-hint">
        {{ question.ratingConfig.hint }}
      </div>
      <div class="rating-slider-container" :class="{ 'disabled': isPainRatingDisabled }">
        <div class="rating-slider-wrapper" ref="sliderWrapperRef">
          <el-slider
            v-model="localAnswer"
            :min="ratingMin"
            :max="ratingMax"
            :step="1"
            :show-tooltip="true"
            :format-tooltip="formatRatingTooltip"
            :disabled="isPainRatingDisabled"
            @change="handleRatingChange"
            class="rating-slider"
          />
        </div>
        <!-- 分数刻度（整数刻度，使用绝对定位精确对齐） -->
        <div class="rating-scale" ref="scaleRef">
          <span
            v-for="score in ratingScaleNumbers"
            :key="score"
            class="rating-scale-number"
            :class="{ 
              'rating-scale-active': Math.abs(localAnswer - score) < 0.05,
              'rating-scale-disabled': isPainRatingDisabled
            }"
            :style="getScaleNumberStyle(score)"
            @click="!isPainRatingDisabled && selectRating(score)"
          >
            {{ score }}
          </span>
        </div>
        <div v-if="question.ratingConfig?.labels && question.ratingConfig.labels.length >= 2" class="rating-semantic-labels">
          <span class="rating-semantic-left">{{ question.ratingConfig.labels[0] }}</span>
          <span class="rating-semantic-right">{{ question.ratingConfig.labels[question.ratingConfig.labels.length - 1] }}</span>
        </div>
      </div>
    </div>
    
    <!-- 数值输入题（带单位） -->
    <div v-if="question.type === 'NUMBER'" class="number-input-question">
      <!-- 时间输入题和下划线输入题：输入框已嵌入到题目文本中，这里不需要单独显示 -->
      <div v-if="!isTimeInput && !isUnderlineInput" class="number-input-group-wjx">
        <!-- 手机号输入：特殊处理 -->
        <input
          v-if="question.title && (question.title.includes('手机号') || question.title.includes('电话'))"
          type="tel"
          :value="localAnswer"
          @input="handlePhoneInput"
          class="number-input-wjx phone-input-wjx"
          placeholder="请输入手机号"
          maxlength="11"
          inputmode="numeric"
        />
        <input
          v-else-if="question.numberConfig?.inputType === 'number'"
          type="number"
          v-model.number="localAnswer"
          :min="question.numberConfig?.min || 0"
          :max="question.numberConfig?.max"
          :placeholder="'请输入'"
          class="number-input-wjx"
          @input="handleNumberInput"
          @blur="validateNumberInput"
        />
        <input
          v-else
          :type="question.numberConfig?.inputType || 'text'"
          v-model="localAnswer"
          :placeholder="'请输入'"
          class="number-input-wjx"
        />
        <span v-if="question.numberConfig?.unit && !(question.title && (question.title.includes('手机号') || question.title.includes('电话')))" class="number-unit-wjx">
          {{ question.numberConfig.unit }}
        </span>
      </div>
    </div>
    
    <!-- Likert量表题（横向单行） -->
    <div v-if="question.type === 'LIKERT_SCALE'" class="likert-scale-question">
      <div class="likert-container">
        <span class="likert-left-label">{{ question.likertConfig?.leftLabel }}</span>
        <el-radio-group
          v-model="localAnswer"
          @change="handleAnswerChange"
          class="likert-options"
        >
          <el-radio
            v-for="(option, idx) in question.likertConfig?.options"
            :key="idx"
            :label="idx"
            class="likert-option"
          >
            {{ option }}
          </el-radio>
        </el-radio-group>
        <span class="likert-right-label">{{ question.likertConfig?.rightLabel }}</span>
      </div>
    </div>
    
    <!-- 矩阵题（纵向列表形式 - 对齐问卷星移动端） -->
    <div v-if="question.type === 'MATRIX'" class="matrix-question-wjx">
      <!-- 选项标题行（只在顶部显示一次） -->
      <div class="matrix-header-row">
        <div
          v-for="(col, colIdx) in question.matrixConfig?.columns"
          :key="colIdx"
          class="matrix-header-cell"
        >
          <span class="matrix-header-text">{{ col }}</span>
        </div>
      </div>
      
      <!-- 子题列表（纵向排列） -->
      <div class="matrix-subquestions">
        <div
          v-for="row in question.matrixConfig?.rows"
          :key="row.id"
          class="matrix-subquestion-item"
        >
          <!-- 子题文本（在上） -->
          <div class="matrix-subquestion-label">
            <img
              v-if="row.imageUrl"
              :src="row.imageUrl"
              class="matrix-image"
              alt=""
            />
            <span>{{ row.label }}</span>
          </div>
          
          <!-- 选项行（在下，5个radio横向均匀分布） -->
          <div class="matrix-options-row">
            <label
              v-for="(col, colIdx) in question.matrixConfig?.columns"
              :key="colIdx"
              class="matrix-option-cell"
              :ref="`matrix-cell-${row.id}-${colIdx}`"
              @click="selectMatrixOption(row.id, col, $event)"
            >
              <input
                type="radio"
                :name="`matrix-${question.id}-${row.id}`"
                :value="col"
                :checked="matrixAnswers[row.id] === col"
                @change="handleMatrixChange(row.id, col)"
                class="matrix-radio-input"
              />
              <span class="matrix-radio-indicator"></span>
            </label>
          </div>
          <!-- 选择提示 tooltip -->
          <div
            v-if="matrixTooltip.visible && matrixTooltip.rowId === row.id"
            class="matrix-tooltip"
            :style="{ left: matrixTooltip.left + 'px', top: matrixTooltip.top + 'px' }"
          >
            {{ matrixTooltip.text }}
          </div>
        </div>
      </div>
    </div>
    
    <!-- 文本题 -->
    <div v-if="question.type === 'TEXT'" class="text-input-wrapper">
      <input
        v-if="question.title && (question.title.includes('手机号') || question.title.includes('电话'))"
        type="tel"
        :value="localAnswer"
        @input="handlePhoneInput"
        class="text-input-wjx"
        placeholder="请输入手机号"
        maxlength="11"
        inputmode="numeric"
      />
      <el-input
        v-else
        type="textarea"
        :rows="10"
        placeholder="请在该区域内作答"
        v-model="localAnswer"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "QuestionnaireItem",
  props: {
    hasError: {
      type: Boolean,
      default: false
    },
    question: {
      type: Object,
      required: true,
      validator: (value) => {
        return value.id && value.title && value.type;
      }
    },
    currentIndex: {
      type: Number,
      required: false  // 改为非必填，因为现在从ID提取
    },
    totalCount: {
      type: Number,
      required: true
    },
    showProgress: {
      type: Boolean,
      default: true
    },
    modelValue: {
      type: [String, Number, Array, Object],
      default: null
    },
    allAnswers: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      timeHours: null,
      timeMinutes: null,
      matrixAnswers: {},  // 矩阵题答案：{ rowId: columnValue }
      sliderWidth: 0,  // 滑块实际宽度，用于计算刻度位置
      customInputValue: '',  // 单选题"其他"选项的自定义输入内容
      matrixTooltip: {
        visible: false,
        text: '',
        rowId: null,
        left: 0,
        top: 0
      },
      matrixTooltipTimer: null  // tooltip 自动隐藏的定时器
    };
  },
  computed: {
    // 从题目ID提取序号（如 "q49" -> 49）
    questionNumber() {
      if (this.question && this.question.id) {
        const match = this.question.id.match(/q(\d+)/);
        if (match && match[1]) {
          return parseInt(match[1], 10);
        }
      }
      // 降级方案：如果无法从ID提取，使用传入的currentIndex
      return this.currentIndex || 0;
    },
    displayTitle() {
      // 数值题：拼接标题和单位，达到与问卷星一致的显示效果
      if (this.question.type === 'NUMBER' && this.question.numberConfig?.unit) {
        const unit = this.question.numberConfig.unit;
        // 如果标题中已经包含单位，直接返回标题
        if (this.question.title.includes(unit)) {
          return this.question.title;
        }
        // 否则拼接标题和单位
        return this.question.title + unit;
      }
      return this.question.title;
    },
    questionTypeText() {
      const typeMap = {
        'SINGLE_CHOICE': '单选题',
        'MULTIPLE_CHOICE': '多选题',
        'RATING': '评分题',
        'TEXT': '文本题',
        'NUMBER': '数值输入',
        'LIKERT_SCALE': '量表题',
        'MATRIX': '矩阵题'
      };
      return typeMap[this.question.type] || '未知题型';
    },
    ratingMin() {
      // 评分最小值
      if (this.question.type === 'RATING' && this.question.ratingConfig) {
        return this.question.ratingConfig.min || 0;
      }
      return 0;
    },
    ratingMax() {
      // 评分最大值
      if (this.question.type === 'RATING' && this.question.ratingConfig) {
        return this.question.ratingConfig.max || 10;
      }
      return 10;
    },
    // 疼痛评分题是否禁用（如果关联的是否题选"否"，则禁用）
    isPainRatingDisabled() {
      if (this.question.type === 'RATING' && this.question.painRatingRelated?.isRatingQuestion) {
        const yesNoQuestionId = this.question.painRatingRelated.yesNoQuestionId;
        const yesNoAnswer = this.allAnswers[yesNoQuestionId];
        // 如果是否题未选择或选择"否"，则禁用评分题
        return !yesNoAnswer || yesNoAnswer === 'no';
      }
      return false;
    },
    ratingScaleNumbers() {
      // 生成刻度数字数组
      if (this.question.type === 'RATING') {
        const numbers = [];
        for (let i = this.ratingMin; i <= this.ratingMax; i++) {
          numbers.push(i);
        }
        return numbers;
      }
      return [];
    },
    isTimeInput() {
      // 判断是否为时间输入（标题或单位包含"小时分钟"或同时包含"小时"和"分钟"）
      // 注意：某些题目的单位在标题中（如"您花了小时分钟"），而不是在 numberConfig.unit 中
      const title = this.question.title || '';
      const unit = this.question.numberConfig?.unit || '';
      const hasHourMinute = title.includes('小时分钟') || unit.includes('小时分钟');
      const hasHourAndMinute = (title.includes('小时') && title.includes('分钟')) || 
                                (unit.includes('小时') && unit.includes('分钟'));
      return this.question.type === 'NUMBER' && (hasHourMinute || hasHourAndMinute);
    },
    timeInputTitleParts() {
      if (!this.isTimeInput) {
        return [{ type: 'text', content: this.displayTitle }];
      }
      
      // 解析题目文本，将 ____ 替换为输入框标记
      let title = this.question.title || '';
      const parts = [];
      
      // 查找第一个 ____小时 的位置
      const hoursIndex = title.indexOf('____小时');
      if (hoursIndex !== -1) {
        // 添加前面的文本
        if (hoursIndex > 0) {
          parts.push({ type: 'text', content: title.substring(0, hoursIndex) });
        }
        // 添加小时输入框标记
        parts.push({ type: 'hours' });
        // 添加"小时"文本
        parts.push({ type: 'text', content: '小时' });
        // 更新 title，移除已处理的部分
        title = title.substring(hoursIndex + '____小时'.length);
      }
      
      // 查找第一个 ____分钟 的位置
      const minutesIndex = title.indexOf('____分钟');
      if (minutesIndex !== -1) {
        // 添加中间的文本
        if (minutesIndex > 0) {
          parts.push({ type: 'text', content: title.substring(0, minutesIndex) });
        }
        // 添加分钟输入框标记
        parts.push({ type: 'minutes' });
        // 添加"分钟"文本
        parts.push({ type: 'text', content: '分钟' });
        // 更新 title，移除已处理的部分
        title = title.substring(minutesIndex + '____分钟'.length);
      }
      
      // 添加剩余的文本
      if (title.length > 0) {
        parts.push({ type: 'text', content: title });
      }
      
      return parts.length > 0 ? parts : [{ type: 'text', content: this.displayTitle }];
    },
    underlineInputTitleParts() {
      if (!this.isUnderlineInput) {
        return [{ type: 'text', content: this.displayTitle }];
      }
      
      // 解析题目文本，将下划线替换为输入框标记
      let title = this.question.title || '';
      const parts = [];
      
      // 匹配模式：多个下划线（至少4个）后跟单位（年/小时/天/个小时）
      const underlinePattern = /(_{4,})(年|小时|天|个小时)/;
      const match = title.match(underlinePattern);
      
      if (match) {
        const underlineIndex = title.indexOf(match[0]);
        const unit = match[2]; // 单位：年/小时/天/个小时
        
        // 添加前面的文本
        if (underlineIndex > 0) {
          parts.push({ type: 'text', content: title.substring(0, underlineIndex) });
        }
        
        // 添加数字输入框标记
        parts.push({ type: 'number' });
        
        // 添加单位文本
        parts.push({ type: 'text', content: unit });
        
        // 添加后面的文本
        const afterIndex = underlineIndex + match[0].length;
        if (afterIndex < title.length) {
          parts.push({ type: 'text', content: title.substring(afterIndex) });
        }
      } else {
        // 如果没有匹配到，返回原文本
        parts.push({ type: 'text', content: this.displayTitle });
      }
      
      return parts.length > 0 ? parts : [{ type: 'text', content: this.displayTitle }];
    },
    isUnderlineInput() {
      // 判断是否为下划线输入题（需要在题目标题的下划线上输入）
      // 识别模式：题目标题中包含多个下划线（____）后跟单位（年/小时/天）
      if (this.question.type !== 'NUMBER') {
        return false;
      }
      const title = this.question.title || '';
      // 匹配模式：多个下划线（至少4个）后跟单位
      const underlinePattern = /_{4,}(年|小时|天|个小时)/;
      return underlinePattern.test(title);
    },
    underlineInputTitleParts() {
      if (!this.isUnderlineInput) {
        return [{ type: 'text', content: this.displayTitle }];
      }
      
      // 解析题目文本，将下划线替换为输入框标记
      let title = this.question.title || '';
      const parts = [];
      
      // 匹配模式：多个下划线（至少4个）后跟单位（年/小时/天/个小时）
      const underlinePattern = /(_{4,})(年|小时|天|个小时)/;
      const match = title.match(underlinePattern);
      
      if (match) {
        const underlineIndex = title.indexOf(match[0]);
        const unit = match[2]; // 单位：年/小时/天/个小时
        
        // 添加前面的文本
        if (underlineIndex > 0) {
          parts.push({ type: 'text', content: title.substring(0, underlineIndex) });
        }
        
        // 添加数字输入框标记
        parts.push({ type: 'number' });
        
        // 添加单位文本
        parts.push({ type: 'text', content: unit });
        
        // 添加后面的文本
        const afterIndex = underlineIndex + match[0].length;
        if (afterIndex < title.length) {
          parts.push({ type: 'text', content: title.substring(afterIndex) });
        }
      } else {
        // 如果没有匹配到，返回原文本
        parts.push({ type: 'text', content: this.displayTitle });
      }
      
      return parts.length > 0 ? parts : [{ type: 'text', content: this.displayTitle }];
    },
    localAnswer: {
      get() {
        // 根据题型返回合适的默认值
        if (this.modelValue !== null && this.modelValue !== undefined) {
          // 矩阵题特殊处理
          if (this.question.type === 'MATRIX') {
            return this.modelValue;
          }
          // 时间输入特殊处理
          if (this.isTimeInput && typeof this.modelValue === 'string') {
            const parts = this.modelValue.split(':');
            if (parts.length === 2) {
              this.timeHours = parseInt(parts[0]) || 0;
              this.timeMinutes = parseInt(parts[1]) || 0;
            }
            return this.modelValue;
          }
          // 文本输入特殊处理：确保返回字符串
          if (this.question.type === 'TEXT') {
            return String(this.modelValue || '');
          }
          // 数值输入特殊处理
          if (this.question.type === 'NUMBER' && this.question.numberConfig?.inputType === 'number') {
            return this.modelValue !== null && this.modelValue !== undefined ? Number(this.modelValue) : null;
          }
          return this.modelValue;
        }
        // 多选返回数组，矩阵题返回对象，数值输入返回 null，文本输入返回空字符串
        if (this.question.type === 'MULTIPLE_CHOICE') {
          return [];
        }
        if (this.question.type === 'MATRIX') {
          // 初始化矩阵答案对象
          if (this.question.matrixConfig?.rows) {
            this.question.matrixConfig.rows.forEach(row => {
              if (!this.matrixAnswers[row.id]) {
                this.matrixAnswers[row.id] = null;
              }
            });
          }
          return {};
        }
        if (this.question.type === 'NUMBER' && this.question.numberConfig?.inputType === 'number') {
          return null; // el-input-number 需要 null 作为初始值
        }
        if (this.question.type === 'TEXT') {
          return ''; // 文本输入返回空字符串，而不是 null
        }
        return null;
      },
      set(value) {
        // 对于文本输入，确保传递的是字符串
        if (this.question.type === 'TEXT') {
          const stringValue = value !== null && value !== undefined ? String(value) : '';
          this.$emit('update:modelValue', stringValue);
        } else {
          this.$emit('update:modelValue', value);
        }
      }
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        // 矩阵题：从 modelValue 初始化 matrixAnswers
        if (this.question.type === 'MATRIX' && newVal && typeof newVal === 'object') {
          this.matrixAnswers = { ...newVal };
        }
        // 时间输入：从 modelValue 初始化 timeHours 和 timeMinutes
        if (this.isTimeInput) {
          if (newVal && typeof newVal === 'string') {
            const parts = newVal.split(':');
            if (parts.length === 2) {
              this.timeHours = parts[0] ? parseInt(parts[0]) : null;
              this.timeMinutes = parts[1] ? parseInt(parts[1]) : null;
            }
          } else if (newVal === null || newVal === undefined) {
            // 初始化为空，允许用户输入
            this.timeHours = null;
            this.timeMinutes = null;
          }
        }
        // 单选题：从 modelValue 初始化 customInputValue（解析"选项值|自定义文本"格式）
        if (this.question.type === 'SINGLE_CHOICE' && newVal && typeof newVal === 'string' && newVal.includes('|')) {
          const parts = newVal.split('|');
          if (parts.length === 2) {
            this.customInputValue = parts[1];
          }
        } else if (this.question.type === 'SINGLE_CHOICE') {
          this.customInputValue = '';
        }
      }
    }
  },
  mounted() {
    // 如果是评分题，计算滑块宽度用于刻度对齐
    if (this.question.type === 'RATING') {
      this.updateSliderWidth();
      window.addEventListener('resize', this.updateSliderWidth);
    }
  },
  beforeUnmount() {
    if (this.question.type === 'RATING') {
      window.removeEventListener('resize', this.updateSliderWidth);
    }
    // 清理 tooltip 定时器
    if (this.matrixTooltipTimer) {
      clearTimeout(this.matrixTooltipTimer);
      this.matrixTooltipTimer = null;
    }
  },
  methods: {
    selectSingleChoice(value, allowCustomInput = false) {
      if (allowCustomInput) {
        // 如果是允许自定义输入的选项，先设置选项值
      this.localAnswer = value;
        // 如果已有自定义内容，立即更新答案
        if (this.customInputValue) {
          this.handleAnswerChange(`${value}|${this.customInputValue}`);
        } else {
          // 否则先只设置选项值，等待用户输入
      this.handleAnswerChange(value);
        }
        // 聚焦输入框
        this.$nextTick(() => {
          const inputEl = this.$refs.customInput;
          if (inputEl && inputEl.length) {
            inputEl[0].focus();
          } else if (inputEl) {
            inputEl.focus();
          }
        });
      } else {
        // 普通选项，直接设置
        this.customInputValue = '';  // 清空自定义输入
        this.localAnswer = value;
        this.handleAnswerChange(value);
      }
    },
    handleAnswerChange(value) {
      this.$emit('update:modelValue', value);
    },
    // 处理自定义输入变化
    handleCustomInputChange() {
      const selectedValue = this.getSelectedOptionValue();
      if (selectedValue && this.customInputValue) {
        // 组合答案：选项值|自定义文本
        this.handleAnswerChange(`${selectedValue}|${this.customInputValue}`);
      } else if (selectedValue) {
        // 如果自定义输入为空，只保存选项值
        this.handleAnswerChange(selectedValue);
      }
    },
    // 从答案中提取选项值（处理"选项值|自定义文本"格式）
    getSelectedOptionValue() {
      if (!this.localAnswer) return null;
      const answerStr = String(this.localAnswer);
      if (answerStr.includes('|')) {
        return answerStr.split('|')[0];
      }
      return answerStr;
    },
    // 处理手机号输入（只允许数字）
    handlePhoneInput(event) {
      // 只允许输入数字，过滤所有非数字字符
      let value = event.target.value.replace(/\D/g, '');
      // 限制长度为11位
      value = value.slice(0, 11);
      // 立即更新输入框的值（防止显示非数字字符）
      event.target.value = value;
      // 更新 modelValue，触发响应式更新
      this.$emit('update:modelValue', value);
      // 使用 nextTick 确保 DOM 更新
      this.$nextTick(() => {
        // 如果输入框的值与期望值不一致，强制更新
        if (event.target && event.target.value !== value) {
          event.target.value = value;
        }
      });
    },
    // 处理数值输入（只验证格式，不限制范围，允许用户继续输入）
    handleNumberInput(event) {
      if (this.question.type !== 'NUMBER' || !this.question.numberConfig) {
        return;
      }
      
      let value = event.target.value;
      
      // 如果输入为空，允许继续输入（不限制，让用户可以删除内容）
      if (value === '' || value === null || value === undefined) {
        this.localAnswer = null;
        this.handleAnswerChange(null);
        return;
      }
      
      // 只验证格式：如果输入的不是数字字符，清除
      // 允许用户输入任何数字，不限制范围（范围限制在失焦时进行）
      const isValidFormat = /^-?\d*\.?\d*$/.test(value);
      
      if (!isValidFormat) {
        event.target.value = '';
        this.localAnswer = null;
        this.handleAnswerChange(null);
        return;
      }
      
      // 转换为数字并更新值（不限制范围，允许用户继续输入）
      let numValue = parseFloat(value);
      
      // 如果转换成功，更新值（不限制范围）
      if (!isNaN(numValue)) {
        this.localAnswer = numValue;
        this.handleAnswerChange(numValue);
      } else {
        // 如果转换失败但格式有效（如用户正在输入负号或小数点），允许继续
        this.localAnswer = value;
        this.handleAnswerChange(value);
      }
    },
    // 验证数值输入（失焦时修正）
    validateNumberInput(event) {
      if (this.question.type !== 'NUMBER' || !this.question.numberConfig) {
        return;
      }
      
      const min = this.question.numberConfig.min;
      const max = this.question.numberConfig.max;
      
      // 如果输入为空，不处理
      if (this.localAnswer === null || this.localAnswer === undefined || this.localAnswer === '') {
        return;
      }
      
      let numValue = parseFloat(this.localAnswer);
      
      // 如果转换失败，重置为 null
      if (isNaN(numValue)) {
        this.localAnswer = null;
        this.handleAnswerChange(null);
        return;
      }
      
      // 限制在范围内
      let corrected = false;
      if (min !== null && min !== undefined && numValue < min) {
        numValue = min;
        corrected = true;
      }
      if (max !== null && max !== undefined && numValue > max) {
        numValue = max;
        corrected = true;
      }
      
      // 如果有修正，更新值
      if (corrected) {
        this.localAnswer = numValue;
        this.handleAnswerChange(numValue);
        // 更新输入框显示
        this.$nextTick(() => {
          if (event.target) {
            event.target.value = numValue;
          }
        });
      }
    },
    formatRatingTooltip(val) {
      // 格式化滑块提示文字，只显示整数，不显示小数
      const intVal = typeof val === 'number' ? Math.round(val) : val;
      
      if (this.question.ratingConfig?.labels && this.question.ratingConfig.labels.length > intVal) {
        const labelIndex = intVal;
        return `${intVal} - ${this.question.ratingConfig.labels[labelIndex]}`;
      }
      return intVal;
    },
    handleRatingChange(value) {
      // 确保评分值是整数
      const intValue = typeof value === 'number' ? Math.round(value) : value;
      this.localAnswer = intValue;
      this.handleAnswerChange(intValue);
    },
    selectRating(score) {
      // 点击刻度数字选择分数（如果未禁用）
      if (!this.isPainRatingDisabled) {
        this.localAnswer = score;
        this.handleAnswerChange(score);
      }
    },
    // 更新滑块宽度（用于计算刻度位置）
    updateSliderWidth() {
      this.$nextTick(() => {
        if (this.$refs.sliderWrapperRef) {
          // 获取滑块容器的实际宽度（排除padding）
          const sliderElement = this.$refs.sliderWrapperRef;
          const computedStyle = window.getComputedStyle(sliderElement);
          const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
          const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
          this.sliderWidth = sliderElement.offsetWidth - paddingLeft - paddingRight;
          
          // 如果找到了滑块的实际DOM元素（el-slider__runway），使用它的宽度更准确
          const sliderRunway = sliderElement.querySelector('.el-slider__runway');
          if (sliderRunway) {
            this.sliderWidth = sliderRunway.offsetWidth;
          }
        }
      });
    },
    // 计算每个刻度数字的位置（使用绝对定位精确对齐）
    getScaleNumberStyle(score) {
      if (!this.sliderWidth || this.ratingScaleNumbers.length === 0) {
        return {};
      }
      
      // 计算该刻度在滑块上的百分比位置
      const totalSteps = this.ratingMax - this.ratingMin;
      const stepPosition = (score - this.ratingMin) / totalSteps;
      
      // 计算绝对位置
      // 刻度容器的padding是10px，所以第一个刻度在10px位置
      // 最后一个刻度在 (容器宽度 - 10px) 位置
      const padding = 10; // 与CSS中的padding保持一致
      const scaleContainerWidth = this.sliderWidth + (padding * 2); // 刻度容器的总宽度
      const position = padding + (this.sliderWidth * stepPosition);
      
      // 使用transform来精确对齐（相对于父容器）
      return {
        position: 'absolute',
        left: `${position}px`,
        transform: 'translateX(-50%)', // 居中对齐，确保数字中心与刻度点对齐
        textAlign: 'center'
      };
    },
    handleHoursInput(event) {
      // 小时输入时处理
      let value = event.target.value;
      
      // 如果输入为空，设置为 null
      if (value === '' || value === null || value === undefined) {
        this.timeHours = null;
        this.handleTimeChange();
        return;
      }
      
      // 只允许数字
      value = value.replace(/\D/g, '');
      this.timeHours = value ? parseInt(value) : null;
      
      this.handleTimeChange();
    },
    validateHours() {
      // 如果为空，不验证
      if (this.timeHours === null || this.timeHours === undefined || this.timeHours === '') {
        this.handleTimeChange();
        return;
      }
      
      // 小时失焦时验证范围（0-23）
      if (this.timeHours < 0) {
        this.timeHours = 0;
      } else if (this.timeHours > 23) {
        this.timeHours = 23;
      }
      
      this.handleTimeChange();
    },
    handleMinutesInput(event) {
      // 分钟输入时处理
      let value = event.target.value;
      
      // 如果输入为空，设置为 null
      if (value === '' || value === null || value === undefined) {
        this.timeMinutes = null;
        this.handleTimeChange();
        return;
      }
      
      // 只允许数字
      value = value.replace(/\D/g, '');
      this.timeMinutes = value ? parseInt(value) : null;
      
      this.handleTimeChange();
    },
    validateMinutes() {
      // 如果为空，不验证
      if (this.timeMinutes === null || this.timeMinutes === undefined || this.timeMinutes === '') {
        this.handleTimeChange();
        return;
      }
      
      // 分钟失焦时验证范围（0-59）
      if (this.timeMinutes < 0) {
        this.timeMinutes = 0;
      } else if (this.timeMinutes > 59) {
        this.timeMinutes = 59;
      }
      
      this.handleTimeChange();
    },
    handleHoursChange(value) {
      // 小时变化时更新（保留兼容性）
      this.timeHours = value !== null && value !== undefined ? value : 0;
      this.handleTimeChange();
    },
    handleMinutesChange(value) {
      // 分钟变化时更新（保留兼容性）
      this.timeMinutes = value !== null && value !== undefined ? value : 0;
      this.handleTimeChange();
    },
    handleTimeChange() {
      // 时间输入：如果两个值都为空，发送 null；否则格式化为 "小时:分钟"
      if ((this.timeHours === null || this.timeHours === undefined || this.timeHours === '') &&
          (this.timeMinutes === null || this.timeMinutes === undefined || this.timeMinutes === '')) {
        this.$emit('update:modelValue', null);
      } else {
        const hours = this.timeHours !== null && this.timeHours !== undefined && this.timeHours !== '' ? this.timeHours : 0;
        const minutes = this.timeMinutes !== null && this.timeMinutes !== undefined && this.timeMinutes !== '' ? this.timeMinutes : 0;
        const timeValue = `${hours}:${minutes}`;
        this.$emit('update:modelValue', timeValue);
      }
    },
    handleMatrixChange(rowId, columnValue) {
      // 矩阵题：更新答案对象（Vue 3 中直接赋值即可）
      this.matrixAnswers[rowId] = columnValue;
      // 发送完整的答案对象
      this.$emit('update:modelValue', { ...this.matrixAnswers });
    },
    handleMatrixRowClick(rowId) {
      // 整行点击：如果该行未选中，默认选中第一列
      if (!this.matrixAnswers[rowId] && this.question.matrixConfig?.columns?.length > 0) {
        const firstColumn = this.question.matrixConfig.columns[0];
        this.handleMatrixChange(rowId, firstColumn);
      }
    },
    selectMatrixOption(rowId, columnValue, event) {
      // 点击单元格：直接选中该选项
      this.handleMatrixChange(rowId, columnValue);
      
      // 显示提示 tooltip
      this.showMatrixTooltip(rowId, columnValue, event);
    },
    /**
     * 显示矩阵题选择提示
     * @param {string} rowId - 行 ID
     * @param {string} columnValue - 选中的列值（选项文本）
     * @param {Event} event - 点击事件对象
     */
    showMatrixTooltip(rowId, columnValue, event) {
      // 清除之前的定时器，避免重复显示
      if (this.matrixTooltipTimer) {
        clearTimeout(this.matrixTooltipTimer);
        this.matrixTooltipTimer = null;
      }
      
      // 获取点击的目标元素
      const target = event?.currentTarget || event?.target?.closest('.matrix-option-cell');
      if (!target) {
        return; // 无法找到目标元素，静默失败
      }
      
      const rect = target.getBoundingClientRect();
      // 获取容器元素（用于相对定位）
      const container = target.closest('.matrix-subquestion-item');
      if (!container) {
        return; // 无法找到容器元素，静默失败
      }
      
      const containerRect = container.getBoundingClientRect();
      
      // 计算 tooltip 位置：相对于容器，在选项上方居中显示
      const left = rect.left - containerRect.left + rect.width / 2;
      const top = rect.top - containerRect.top - 10; // 在选项上方 10px
      
      // 更新 tooltip 状态
      this.matrixTooltip = {
        visible: true,
        text: columnValue,
        rowId: rowId,
        left: left,
        top: top
      };
      
      // 2 秒后自动隐藏
      this.matrixTooltipTimer = setTimeout(() => {
        if (this.matrixTooltip) {
          this.matrixTooltip.visible = false;
        }
        this.matrixTooltipTimer = null;
      }, 2000);
    }
  }
};
</script>

<style lang="less" scoped>
.questionnaire-item {
  font-size: 16px;
  padding: 16px;
  padding-bottom: 20px;
  line-height: 1.6;
  color: #333;
  background-color: #fff;
  margin-bottom: 12px;
  border-radius: 0;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.3s, box-shadow 0.3s;
  
  &.has-error {
    border-left: 4px solid #ff4d4f;
    background-color: #fff1f0;
    box-shadow: 0 2px 8px rgba(255, 77, 79, 0.15);
    
    .question-title {
      .error-hint {
        color: #ff4d4f;
        font-size: 14px;
        font-weight: normal;
        margin-left: 8px;
      }
    }
  }
  
  .question-title {
    display: block;
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.6;
    color: #333;
    
    > span {
      display: inline;
    }
    
    .error-hint {
      color: #ff4d4f;
      font-size: 14px;
      font-weight: normal;
      margin-left: 8px;
    }
  }
  
  .required-mark {
    color: #ff0000;
    margin-right: 2px;
    font-weight: normal;
    display: inline !important;
  }
  
  .question-number {
    margin-right: 2px;
    display: inline !important;
  }
  
  .question-text {
    font-weight: 600;
    display: inline !important;
    word-wrap: break-word;
    word-break: break-word;
    
    // 题目文本中嵌入的输入框样式
    &.question-text-with-inputs {
      .time-input-inline {
        display: inline-block;
        width: 60px;
        height: 28px;
        padding: 2px 4px;
        margin: 0 2px;
        border: none;
        border-bottom: 2px solid #333;
        background-color: transparent;
        font-size: 16px;
        color: #333;
        text-align: center;
        outline: none;
        transition: border-color 0.2s;
        vertical-align: baseline;
        -webkit-appearance: none;
        appearance: none;
        
        &:focus {
          border-bottom-color: #0095ff;
        }
        
        &::placeholder {
          color: #ccc;
        }
        
        // 移除数字输入框的上下箭头
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        &[type=number] {
          -moz-appearance: textfield;
        }
      }
      
      .underline-input-inline {
        display: inline-block;
        width: 80px;
        height: 28px;
        padding: 2px 4px;
        margin: 0 2px;
        border: none;
        border-bottom: 2px solid #333;
        background-color: transparent;
        font-size: 16px;
        color: #333;
        text-align: center;
        outline: none;
        transition: border-color 0.2s;
        vertical-align: baseline;
        -webkit-appearance: none;
        appearance: none;
        
        &:focus {
          border-bottom-color: #0095ff;
        }
        
        &::placeholder {
          color: #ccc;
        }
        
        // 移除数字输入框的上下箭头
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        &[type=number] {
          -moz-appearance: textfield;
        }
      }
    }
  }
  
  // 单选题样式 - 对齐问卷星移动端
  .single-choice-wrapper {
    margin-top: 8px;
  }
  
  .single-choice-option {
    display: flex;
    flex-direction: column;
    padding: 12px 16px;
    margin-bottom: 8px;
    background-color: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    min-height: 44px;
    
    &:hover {
      background-color: #f5f5f5;
      border-color: #0095ff;
    }
    
    &.selected {
      background-color: #f0f8ff;
      border-color: #0095ff;
      
      .option-radio {
        border-color: #0095ff;
        background-color: #0095ff;
        
        &::after {
          opacity: 1;
        }
      }
    }
    
    // 带自定义输入的选项样式调整
    &.has-custom-input.selected {
      padding-bottom: 8px;
    }
    
    .option-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }
    
    .option-label {
      flex: 1;
      font-size: 16px;
      color: #333;
      line-height: 1.5;
    }
    
    .option-radio {
      width: 20px;
      height: 20px;
      border: 2px solid #d0d0d0;
      border-radius: 50%;
      position: relative;
      flex-shrink: 0;
      transition: all 0.2s;
      
      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #fff;
        opacity: 0;
        transition: opacity 0.2s;
      }
    }
    
    // 自定义输入框容器
    .custom-input-wrapper {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #e8f4ff;
      
      .custom-input {
        width: 100%;
        height: 40px;
        padding: 0 12px;
        border: 1px solid #0095ff;
        border-radius: 4px;
        font-size: 16px;
        color: #333;
        background-color: #fff;
        outline: none;
        transition: border-color 0.2s;
        box-sizing: border-box;
        
        &:focus {
          border-color: #0095ff;
          box-shadow: 0 0 0 2px rgba(0, 149, 255, 0.1);
        }
        
        &::placeholder {
          color: #999;
        }
      }
    }
    
    // PC端适配：hover效果更明显
    @media (min-width: 768px) {
      &:hover {
        background-color: #f0f8ff;
        border-color: #0095ff;
        box-shadow: 0 2px 4px rgba(0, 149, 255, 0.1);
      }
    }
  }
  
  // 多选样式 - 保持原有样式但调整间距
  .el-checkbox {
    width: 100%;
    padding: 8px 10px;
    font-size: 16px;
    margin-right: 0;
    margin-bottom: 8px;
    height: auto;
    display: flex !important;
    align-items: center !important;
    border-bottom: 1px solid #f0f0f0;
  }
  
  .el-checkbox__input {
    flex-shrink: 0;
  }
  
  .el-checkbox__label {
    white-space: normal !important;
    word-wrap: break-word;
    word-break: break-word;
    line-height: 1.6 !important;
    flex: 1;
  }
  
  // 评分题样式（0-10分横向滑块）
  .rating-question {
    padding: 20px 0;
    
    .rating-hint {
      font-size: 12px;
      color: #999;
      margin-bottom: 12px;
    }
    
    .rating-slider-container {
      padding: 20px 0;
      
      .rating-slider-wrapper {
        margin-bottom: 12px;
        padding: 0 10px; // 与刻度数字的padding保持一致，确保对齐
        
        .rating-slider {
          width: 100%;
          
          :deep(.el-slider__runway) {
            height: 6px;
            background-color: #e4e7ed;
          }
          
          :deep(.el-slider__bar) {
            height: 6px;
            background-color: #409EFF;
          }
          
          :deep(.el-slider__button-wrapper) {
            width: 20px;
            height: 20px;
            top: -7px;
            
            .el-slider__button {
              width: 20px;
              height: 20px;
              border: 2px solid #409EFF;
              background-color: #fff;
            }
          }
        }
      }
      
      .rating-scale {
        position: relative;
        margin-bottom: 8px;
        padding: 0 10px; // 与滑块的padding保持一致
        height: 24px; // 固定高度，确保数字有足够空间
        min-height: 24px;
        
        .rating-scale-number {
          position: absolute; // 使用绝对定位
          top: 0;
          font-size: 12px;
          color: #666;
          cursor: pointer;
          user-select: none;
          transition: all 0.2s;
          white-space: nowrap; // 防止文字换行
          min-width: 20px; // 确保点击区域足够大
          
          &:hover {
            color: #409EFF;
            font-weight: 500;
          }
          
          &.rating-scale-active {
            color: #409EFF;
            font-weight: bold;
            font-size: 14px;
          }
          
          &.rating-scale-disabled {
            color: #c0c4cc;
            cursor: not-allowed;
            opacity: 0.5;
            
            &:hover {
              color: #c0c4cc;
              font-weight: normal;
            }
          }
        }
      }
      
      &.disabled {
        opacity: 0.6;
        pointer-events: none;
      }
      
      .rating-labels {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #666;
        margin-bottom: 8px;
        
        .rating-label-left,
        .rating-label-right {
          font-weight: 500;
        }
      }
      
      .rating-semantic-labels {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #999;
        
        .rating-semantic-left,
        .rating-semantic-right {
          font-style: italic;
        }
      }
    }
    
    .pain-rating-hint {
      padding: 8px 12px;
      margin-bottom: 12px;
      background-color: #f0f2f5;
      border-left: 3px solid #909399;
      color: #606266;
      font-size: 13px;
      border-radius: 4px;
    }
    
    &.pain-rating-disabled {
      .rating-slider-container {
        opacity: 0.6;
      }
    }
  }
  
  // 数值输入题样式 - 对齐问卷星移动端
  .number-input-question {
    padding: 8px 0;
    margin-top: 8px;
    
    .number-input-group-wjx {
      display: flex;
      align-items: center;
      gap: 4px;
      width: 100%;

      .number-input-wjx {
        // 手机号输入框：占满容器
        &.phone-input-wjx {
          width: 100% !important;
          min-width: 100%;
          max-width: 100%;
          height: 44px;
          padding: 0 16px;
          font-size: 16px;
        }
        // 普通数值输入框：短框
        &:not(.phone-input-wjx) {
          width: 100px;
          height: 40px;
          padding: 0 12px;
          font-size: 16px;
        }
        border: 1px solid #d0d0d0;
        border-radius: 4px;
        color: #333;
        background-color: #fff;
        outline: none;
        transition: border-color 0.2s;
        box-sizing: border-box;
        -webkit-appearance: none;
        appearance: none;

        &:focus {
          border-color: #0095ff;
        }

        &::placeholder {
          color: #999;
        }
      }
      
      .number-unit-wjx {
        color: #666;
        font-size: 16px;
        margin-left: 2px;
        white-space: nowrap;
      }
    }
    
    // PC端适配：输入框稍宽
    @media (min-width: 768px) {
      .number-input-group-wjx {
        .number-input-wjx {
          width: 120px;
        }
      }
    }
    
    .number-input-group {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .number-input {
        width: 150px;
      }
      
      .number-unit {
        color: #666;
        font-size: 14px;
      }
    }
    
    .time-input-group {
      display: flex !important;
      align-items: center;
      gap: 8px;
      padding: 10px 0;
      min-height: 40px;
      visibility: visible !important;
      opacity: 1 !important;
      
      .time-input-underline {
        width: 80px;
        height: 32px;
        padding: 4px 8px;
        border: none;
        border-bottom: 2px solid #333;
        background-color: transparent;
        font-size: 16px;
        color: #333;
        text-align: center;
        outline: none;
        transition: border-color 0.2s;
        -webkit-appearance: none;
        appearance: none;
        
        &:focus {
          border-bottom-color: #0095ff;
        }
        
        &::placeholder {
          color: #ccc;
        }
        
        // 移除数字输入框的上下箭头
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        &[type=number] {
          -moz-appearance: textfield;
        }
      }
      
      .time-unit {
        color: #666;
        font-size: 14px;
        margin-left: 2px;
        display: inline-block;
        visibility: visible !important;
      }
    }
  }
  
  // Likert量表题样式（横向单行）
  .likert-scale-question {
    padding: 20px 0;
    
    .likert-container {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      
      .likert-left-label,
      .likert-right-label {
        font-size: 12px;
        color: #666;
        white-space: nowrap;
      }
      
      .likert-options {
        flex: 1;
        display: flex;
        gap: 8px;
        justify-content: space-between;
        
        .likert-option {
          flex: 1;
          margin: 0;
          
          :deep(.el-radio__label) {
            font-size: 12px;
            padding-left: 4px;
          }
        }
      }
    }
  }
  
  // 矩阵题样式 - 纵向列表形式（对齐问卷星移动端）
  .matrix-question-wjx {
    padding: 12px 0;
    margin-top: 8px;
    background-color: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    
    // 选项标题行（只在顶部显示一次）
    .matrix-header-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      background-color: #f5f5f5;
      border-bottom: 1px solid #e0e0e0;
      margin-bottom: 0;
      
      .matrix-header-cell {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        min-width: 0; // 允许文字换行
        
        .matrix-header-text {
          font-size: 14px;
          color: #666;
          line-height: 1.5;
          word-break: break-all;
          white-space: normal;
          text-align: center;
        }
      }
    }
    
    // 子题列表（纵向排列）
    .matrix-subquestions {
      padding: 0;
      
      .matrix-subquestion-item {
        padding: 16px;
        border-bottom: 1px solid #f0f0f0;
        position: relative;
        
        &:last-child {
          border-bottom: none;
        }
        
        // 子题文本（在上）
        .matrix-subquestion-label {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 12px;
          text-align: left;
          
          .matrix-image {
            max-width: 40px;
            max-height: 40px;
            object-fit: contain;
            flex-shrink: 0;
          }
          
          span {
            font-size: 16px;
            color: #333;
            line-height: 1.6;
            flex: 1;
          }
        }
        
        // 选项行（在下，5个radio横向均匀分布）
        .matrix-options-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 0;
          
          .matrix-option-cell {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            padding: 8px 4px;
            min-height: 44px;
            position: relative;
            
            .matrix-radio-input {
              position: absolute;
              opacity: 0;
              width: 0;
              height: 0;
              margin: 0;
              pointer-events: none;
            }
            
            .matrix-radio-indicator {
              width: 20px;
              height: 20px;
              border: 2px solid #d0d0d0;
              border-radius: 50%;
              position: relative;
              transition: all 0.2s;
              flex-shrink: 0;
              background-color: #fff;
              
              &::after {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) scale(0);
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: #0095ff;
                transition: transform 0.2s;
                opacity: 0;
              }
            }
            
            .matrix-radio-input:checked + .matrix-radio-indicator {
              border-color: #0095ff;
              background-color: #0095ff;
              
              &::after {
                transform: translate(-50%, -50%) scale(1);
                opacity: 1;
                background-color: #fff;
              }
            }
            
            &:hover .matrix-radio-indicator {
              border-color: #0095ff;
            }
            
            .matrix-radio-input:focus + .matrix-radio-indicator {
              outline: 2px solid rgba(0, 149, 255, 0.2);
              outline-offset: 2px;
            }
          }
        }
        
        // 选择提示 tooltip
        .matrix-tooltip {
          position: absolute;
          background-color: #0095ff;
          color: #fff;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 14px;
          white-space: nowrap;
          z-index: 1000;
          pointer-events: none;
          transform: translateX(-50%);
          animation: tooltipFadeIn 0.2s ease-out;
          
          // 小三角箭头（指向下方）
          &::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 6px solid #0095ff;
          }
        }
      }
    }
  }
  
  // tooltip 淡入动画
  @keyframes tooltipFadeIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
  
  // 文本题样式
  .text-input-wrapper {
    margin-top: 8px;
    width: 100%;
    display: block;
    
    .text-input-wjx {
      display: block;
      width: 100% !important;
      min-width: 100%;
      max-width: 100%;
      height: 44px; // 增加高度，提升移动端体验
      padding: 0 16px; // 增加左右padding
      border: 1px solid #d0d0d0;
      border-radius: 4px;
      font-size: 16px;
      color: #333;
      background-color: #fff;
      transition: border-color 0.2s;
      box-sizing: border-box;
      -webkit-appearance: none; // 移除 iOS 默认样式
      appearance: none;
      margin: 0; // 确保没有外边距
      
      &:focus {
        outline: none;
        border-color: #0095ff;
      }
      
      &::placeholder {
        color: #999;
      }
    }
  }
  
  .el-textarea {
    margin-top: 10px;
  }
}
</style>
